import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Layers, Calendar, MapPin, BookOpen, CheckCircle2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { FiHeart } from "react-icons/fi";
import BookingModal from "./BookingModal";
import "./ChatWidget.css";

const ChatWidget = ({
    onToggleSampleSelectionMode,
    onToggleLikedPanel,
    onCheckoutFavorites,
    onOpenSamplesPage,
    selectedSamples = [],
    likedVisuals = [],
    // Controlled open state — optional; falls back to internal state
    open,
    onOpenChange,
}) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = open !== undefined ? open : internalOpen;
    const setIsOpen = (val) => {
        setInternalOpen(val);
        onOpenChange && onOpenChange(val);
    };
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState("");
    const [selectedBookingShowroom, setSelectedBookingShowroom] = useState("nairobi");
    const [actionTaken, setActionTaken] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        // Simple session management
        let sid = localStorage.getItem("chat_session_id");
        if (!sid) {
            sid = `session_${Date.now()}`;
            localStorage.setItem("chat_session_id", sid);
        }
        setSessionId(sid);

        // Initial welcome message if no history
        if (messages.length === 0) {
            setMessages([{
                id: "welcome",
                role: "assistant",
                content: "Hi! How can we help you today?",
                timestamp: new Date()
            }]);
        }
    }, [messages.length]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const sendToWebhook = async (userMessage, intent) => {
        setIsLoading(true);
        const userMsg = { id: Date.now().toString(), role: "user", content: userMessage, timestamp: new Date() };
        setMessages(prev => [...prev, userMsg]);

        try {
            const response = await fetch("http://localhost:5000/api/chatbot", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    sessionId,
                    message: userMessage,
                    intent: intent || "general"
                }),
            });

            const data = await response.json();

            // Check for special action codes from n8n
            if (data.action === 'SHOW_BOOKING') {
                setIsBookingModalOpen(true);
            }

            const assistantMsg = {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: data.output || "Thank you! We'll get back to you.",
                type: data.type || "text", // Support for special card types
                timestamp: new Date()
            };
            setMessages(prev => [...prev, assistantMsg]);
        } catch (error) {
            console.error("Chat error:", error);
            const errorMsg = {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: "Sorry, I'm having trouble connecting. Feel free to contact us on WhatsApp directly!",
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickAction = (action) => {
        setActionTaken(true);
        const cartLen = selectedSamples.length;
        const favLen = likedVisuals.length;

        switch (action) {
            case 'request_sample':
                if (cartLen === 0 && favLen === 0) {
                    onToggleSampleSelectionMode();
                    setMessages(prev => [...prev, {
                        id: Date.now().toString(),
                        role: "assistant",
                        content: "I've enabled Sample Selection Mode for you! Browse our collections and click 'Add to Cart' on the materials you like. You can select up to 5 samples.",
                        timestamp: new Date()
                    }]);
                } else if (cartLen === 0 && favLen > 0) {
                    setMessages(prev => [...prev, {
                        id: Date.now().toString(),
                        role: "assistant",
                        content: `I see your cart is empty, but you have ${favLen} favorite colors! Would you like to request samples for all of them right now?`,
                        timestamp: new Date(),
                        actions: [{ id: 'checkout_favorites', label: 'Request All Favorites', primary: true }]
                    }]);
                } else {
                    const cartList = selectedSamples.map(s => s.name).join(", ");
                    const favList = likedVisuals.map(v => v.label).join(", ");

                    setMessages(prev => [...prev, {
                        id: Date.now().toString(),
                        role: "assistant",
                        content: `You're doing great! Here's a quick summary:\n\n🛒 **In Cart (${cartLen}):** ${cartList}\n\n❤️ **In Favorites (${favLen}):** ${favList || 'None yet'}\n\nReady to finalize your request?`,
                        timestamp: new Date(),
                        actions: [
                            { id: 'go_to_checkout', label: 'Go to Checkout', primary: true },
                            { id: 'checkout_favorites', label: 'Add Favorites & Checkout', primary: false }
                        ]
                    }]);
                }
                break;
            case 'view_favorites':
                onToggleLikedPanel();
                break;
            case 'site_visit':
                setMessages(prev => [...prev,
                {
                    id: Date.now().toString(),
                    role: "user",
                    content: "Hi! I'd like to schedule a showroom visit to see your stone surfaces.",
                    timestamp: new Date()
                },
                {
                    id: (Date.now() + 1).toString(),
                    role: "assistant",
                    content: "Which showroom would you like to visit?",
                    type: "showroom_chooser",
                    timestamp: new Date()
                }
                ]);
                break;
            case 'directions':
                setMessages(prev => [...prev,
                {
                    id: Date.now().toString(),
                    role: "user",
                    content: "How do I get to the showroom?",
                    timestamp: new Date()
                },
                {
                    id: (Date.now() + 1).toString(),
                    role: "assistant",
                    content: "Here are directions to our showrooms:",
                    type: "directions_card",
                    timestamp: new Date()
                }
                ]);
                break;
            case 'catalogue':
                window.open("https://drive.google.com/file/d/1v-VfHlUZxiUBZ6TH-qBq6f0NHZ5L3a6c/view?usp=sharing", "_blank");
                break;
            case 'whatsapp':
                window.open("https://wa.me/254745969500", "_blank");
                break;
            default:
                break;
        }
    };

    const handleInChatMessage = (actionId) => {
        if (actionId === 'checkout_favorites') {
            onCheckoutFavorites();
            setIsOpen(false);
        } else if (actionId === 'go_to_checkout') {
            onOpenSamplesPage();
            setIsOpen(false);
        } else if (actionId === 'book_nairobi') {
            setSelectedBookingShowroom("nairobi");
            setIsBookingModalOpen(true);
        } else if (actionId === 'book_diani') {
            setSelectedBookingShowroom("diani");
            setIsBookingModalOpen(true);
        }
    };


    const handleBookingSuccess = (summary) => {
        // Add user summary message
        const userMsg = {
            id: Date.now().toString(),
            role: "user",
            content: summary,
            timestamp: new Date()
        };

        // Add assistant confirmation message
        const confirmMsg = {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content: "Appointment Confirmed!",
            type: "appointment_confirmed",
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMsg, confirmMsg]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (message.trim() && !isLoading) {
            const msg = message.trim();
            setMessage("");
            sendToWebhook(msg);
        }
    };

    const quickActions = [
        { id: 'request_sample', label: 'Request a sample', icon: <Layers size={18} /> },
        { id: 'site_visit', label: 'Schedule a showroom visit', icon: <Calendar size={18} /> },
        { id: 'directions', label: 'Get showroom directions', icon: <MapPin size={18} /> },
        { id: 'catalogue', label: 'Request catalogue', icon: <BookOpen size={18} /> },
        { id: 'view_favorites', label: 'View favorites', icon: <FiHeart size={18} /> },
    ];

    return (
        <>
            {/* Toggle Button */}
            <div className="chat-toggle-wrapper">
                {!isOpen && (
                    <span className="chat-toggle-label" onClick={() => setIsOpen(true)}>Chat with us</span>
                )}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="chat-toggle-btn"
                    aria-label="Toggle Chat"
                >
                    {isOpen ? <X size={24} /> : <FaWhatsapp size={28} />}
                </button>
            </div>

            {/* Chat Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.9 }}
                        className="chat-window"
                    >
                        <div className="chat-header">
                            <div className="chat-header-info">
                                <div className="chat-header-avatar">
                                    <img src="/EF-Newlogo.png" alt="EF" />
                                </div>
                                <div className="chat-header-text">
                                    <span className="chat-header-name">Elegant Fittings</span>
                                    <span className="chat-header-status">Online</span>
                                </div>
                            </div>
                            <button className="chat-close-btn" onClick={() => setIsOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="chat-messages">
                            {messages.map((msg) => (

                                <div key={msg.id} className={`chat-message-row ${msg.role === 'user' ? 'user' : 'assistant'}`}>
                                    <div className="chat-message-bubble">
                                        {msg.type === 'showroom_chooser' ? (
                                            <div className="showroom-chooser-card">
                                                <div className="hours-card-header">
                                                    <Calendar size={18} />
                                                    <span>Choose a Showroom</span>
                                                </div>
                                                <div className="showroom-options">
                                                    <button className="showroom-option-btn" onClick={() => handleInChatMessage('book_nairobi')}>
                                                        <strong>Nairobi</strong>
                                                        <span>Woodvale Lane, Ridgeways</span>
                                                    </button>
                                                    <button className="showroom-option-btn" onClick={() => handleInChatMessage('book_diani')}>
                                                        <strong>Diani</strong>
                                                        <span>Pioneer Mall, 1st Floor</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : msg.type === 'directions_card' ? (
                                            <div className="directions-card">
                                                <div className="hours-card-header">
                                                    <MapPin size={18} />
                                                    <span>Our Showrooms</span>
                                                </div>
                                                <div className="directions-showroom">
                                                    <strong>Nairobi · Ridgeways</strong>
                                                    <p>Woodvale Lane, Ridgeways, Kiambu Road</p>
                                                    <a href="https://maps.app.goo.gl/5pZaYYBpDS2G1qtC9" target="_blank" rel="noopener noreferrer" className="directions-link">
                                                        Get Directions →
                                                    </a>
                                                </div>
                                                <div className="directions-showroom">
                                                    <strong>Diani · Pioneer Mall</strong>
                                                    <p>Pioneer Mall, 1st Floor, Diani Beach Road Junction</p>
                                                    <a href="https://www.google.com/search?q=Elegant+Fittings+Limited+-+Diani" target="_blank" rel="noopener noreferrer" className="directions-link">
                                                        Get Directions →
                                                    </a>
                                                </div>
                                            </div>
                                        ) : msg.type === 'business_hours' ? (
                                            <div className="business-hours-card">
                                                <div className="hours-card-header">
                                                    <Calendar size={18} />
                                                    <span>Business Hours — Nairobi</span>
                                                </div>
                                                <div className="hours-list">
                                                    <div className="hours-row"><span>Mon:</span> <span>7:30 AM - 4:30 PM</span></div>
                                                    <div className="hours-row"><span>Tue - Fri:</span> <span>7:30 AM - 6:00 PM</span></div>
                                                    <div className="hours-row"><span>Sat:</span> <span>8:00 AM - 4:00 PM</span></div>
                                                    <div className="hours-row"><span>Sun:</span> <span>Closed</span></div>
                                                </div>
                                                <button className="schedule-visit-btn" onClick={() => setIsBookingModalOpen(true)}>
                                                    <Calendar size={16} /> Schedule Visit
                                                </button>
                                            </div>
                                        ) : msg.type === 'appointment_confirmed' ? (
                                            <div className="appointment-confirmed-card">
                                                <div className="confirm-header">
                                                    <div className="check-icon">
                                                        <CheckCircle2 size={20} color="#10b981" />
                                                    </div>
                                                    <span>Appointment Confirmed!</span>
                                                </div>
                                                <p>Need more help? Just type your question below.</p>
                                            </div>
                                        ) : (
                                            <div className="message-text">
                                                {msg.content}
                                            </div>
                                        )}

                                        {msg.actions && (
                                            <div className="chat-message-actions">
                                                {msg.actions.map(action => (
                                                    <button
                                                        key={action.id}
                                                        className={`chat-action-btn ${action.primary ? 'primary' : 'secondary'}`}
                                                        onClick={() => handleInChatMessage(action.id)}
                                                    >
                                                        {action.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        <div className="chat-message-time">
                                            Now
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {isLoading && (
                                <div className="chat-message-row assistant">
                                    <div className="chat-message-bubble loading">
                                        <div className="typing-indicator">
                                            <span></span><span></span><span></span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Quick actions — vertical on load, horizontal after first action */}
                        <div className={`quick-actions-container${actionTaken ? ' is-horizontal' : ''}`}>
                            {quickActions.map(action => (
                                <button
                                    key={action.id}
                                    className="quick-action-btn"
                                    onClick={() => handleQuickAction(action.id)}
                                >
                                    <span className="quick-action-icon">{action.icon}</span>
                                    <span className="quick-action-label">{action.label}</span>
                                </button>
                            ))}
                            <button className="whatsapp-action-btn" onClick={() => handleQuickAction('whatsapp')}>
                                <FaWhatsapp size={16} />
                                <span>WhatsApp</span>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="chat-input-area">
                            <input
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Type a message"
                                className="chat-input"
                            />
                            <button type="submit" className="chat-send-btn" disabled={!message.trim() || isLoading}>
                                <Send size={20} />
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            <BookingModal
                isOpen={isBookingModalOpen}
                onClose={() => setIsBookingModalOpen(false)}
                sessionId={sessionId}
                likedVisuals={likedVisuals}
                selectedSamples={selectedSamples}
                onBookingSuccess={handleBookingSuccess}
                showroom={selectedBookingShowroom}
            />
        </>
    );
};

export default ChatWidget;
